import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  Sparkles,
  ClipboardList,
  BookOpen,
  Activity,
  ChevronRight,
  UserPlus,
  AlertCircle,
  Search,
  Briefcase,
  ArrowRight,
  ArrowLeft,
  Phone,
  MapPin
} from 'lucide-react';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTreatment } from '../../context/TreatmentContext';
import '../../styles/treatment.css';

const TreatmentDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [errorStats, setErrorStats] = useState(null);
  
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  
  const [patientPlans, setPatientPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  const { isAuthenticated } = useAuth();
  const { currentPatient, setCurrentPatient, clearCurrentPatient } = useTreatment();
  const navigate = useNavigate();

  // Load Catalog Statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);
        setErrorStats(null);
        const data = await apiService.getCatalogStatistics();
        setStats(data);
      } catch (err) {
        setErrorStats(err.message || 'Failed to load statistics');
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  // Load Patients list for selector directory
  useEffect(() => {
    if (!isAuthenticated()) return;
    const fetchPatients = async () => {
      setLoadingPatients(true);
      try {
        const data = await apiService.getPatients();
        setPatients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load patients for directory:', err);
      } finally {
        setLoadingPatients(false);
      }
    };
    fetchPatients();
  }, [isAuthenticated, currentPatient]);

  // Load Active Patient Plans if patient is selected
  useEffect(() => {
    if (!currentPatient?.id) {
      setPatientPlans([]);
      return;
    }
    const fetchPatientPlans = async () => {
      setLoadingPlans(true);
      try {
        const data = await apiService.getTreatmentPlansByPatientId(currentPatient.id);
        setPatientPlans(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load active patient plans:', err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPatientPlans();
  }, [currentPatient?.id]);

  // Initials helper
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Filter patients by name or phone
  const filteredPatients = patients.filter((p) =>
    p.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
    (p.contactNumber && p.contactNumber.includes(patientSearch))
  );

  // Outer Layer: No active patient
  const renderDashboardOuter = () => (
    <>
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Clinical Dashboard</h1>
            <p>
              Search or register patient profiles to perform Prakriti assessments and generate personalized treatments.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="grid grid-2">
            {/* Patient Search Directory card */}
            <div className="card patient-directory-card" style={{ padding: '1.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h2 style={{ fontSize: '1.25rem', margin: 0 }}>Patient Directory</h2>
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => navigate('/treatment/patients')}
                >
                  <UserPlus size={16} /> Register Patient
                </button>
              </div>

              <div className="patient-search-box">
                <Search size={18} className="patient-search-icon" />
                <input
                  type="text"
                  className="patient-search-input"
                  placeholder="Search patient by name or contact number..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                />
              </div>

              {loadingPatients ? (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
                  Loading patient profiles...
                </div>
              ) : filteredPatients.length > 0 ? (
                <div className="patient-directory-results">
                  {filteredPatients.map((p) => (
                    <div
                      key={p.id}
                      className="patient-directory-item"
                      onClick={() => setCurrentPatient(p)}
                    >
                      <div className="patient-directory-info">
                        <h4>{p.name}</h4>
                        <div className="patient-directory-meta">
                          {p.age}y, {p.gender} {p.prakriti && ` · Prakriti: ${p.prakriti}`}
                        </div>
                      </div>
                      <ChevronRight size={18} style={{ color: 'var(--primary-color)' }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
                  No matching patients found.
                </div>
              )}
            </div>

            {/* Quick Links / Resources */}
            <div className="card" style={{ padding: '1.75rem' }}>
              <h2 style={{ fontSize: '1.25rem', marginBottom: '1.25rem' }}>AYUSH Catalog & Tools</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Link to="/treatment/catalog" className="treatment-quicklink-card card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <BookOpen size={24} style={{ color: 'var(--primary-color)' }} />
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-primary)' }}>Evidence Catalog</h4>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Browse herbal medicines, yoga asanas, and dietary guides.
                      </p>
                    </div>
                    <ChevronRight size={18} />
                  </div>
                </Link>
                <Link to="/treatment/patients" className="treatment-quicklink-card card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Users size={24} style={{ color: 'var(--primary-color)' }} />
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--text-primary)' }}>All Patient Files</h4>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Review full histories, contact cards, and clinical notes.
                      </p>
                    </div>
                    <ChevronRight size={18} />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Overview */}
      {!loadingStats && !errorStats && stats && (
        <section className="section">
          <div className="container">
            <h2 className="section-title">Clinical Statistics Overview</h2>
            <div className="treatment-stats-grid">
              <div className="treatment-stat-card">
                <Users size={28} />
                <span className="treatment-stat-value">{stats.totalPatients ?? 0}</span>
                <span className="treatment-stat-label">Total Patients</span>
              </div>
              <div className="treatment-stat-card">
                <ClipboardList size={28} />
                <span className="treatment-stat-value">{stats.totalTreatmentPlans ?? 0}</span>
                <span className="treatment-stat-label">Treatment Plans</span>
              </div>
              <div className="treatment-stat-card">
                <FileText size={28} />
                <span className="treatment-stat-value">{stats.totalConditions ?? 0}</span>
                <span className="treatment-stat-label">Conditions</span>
              </div>
              <div className="treatment-stat-card">
                <BookOpen size={28} />
                <span className="treatment-stat-value">{stats.totalHerbalMedicines ?? 0}</span>
                <span className="treatment-stat-label">Medicines</span>
              </div>
              <div className="treatment-stat-card">
                <Activity size={28} />
                <span className="treatment-stat-value">{stats.totalYogaAsanas ?? 0}</span>
                <span className="treatment-stat-label">Yoga Asanas</span>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );

  // Inside Layer: Patient specific Workspace
  const renderDashboardInner = () => (
    <div className="container" style={{ paddingTop: '2rem' }}>
      {/* Patient Dossier Header Card */}
      <div className="patient-dossier-card">
        <div className="patient-dossier-header">
          <div className="patient-dossier-profile">
            <div className="patient-dossier-avatar">
              {getInitials(currentPatient.name)}
            </div>
            <div className="patient-dossier-details">
              <h2>{currentPatient.name}</h2>
              <div className="patient-dossier-meta-grid">
                <span className="patient-dossier-meta-item">Age: {currentPatient.age}y</span>
                <span className="patient-dossier-meta-item">Gender: {currentPatient.gender}</span>
                {currentPatient.contactNumber && (
                  <span className="patient-dossier-meta-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Phone size={12} /> {currentPatient.contactNumber}
                  </span>
                )}
                {currentPatient.address && (
                  <span className="patient-dossier-meta-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <MapPin size={12} /> {currentPatient.address}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div>
            <button
              className="btn btn-outline"
              onClick={clearCurrentPatient}
            >
              <ArrowLeft size={16} /> Switch Patient
            </button>
          </div>
        </div>
      </div>

      {/* Patient Workspace Modules Grid */}
      <div className="patient-workspace-grid">
        {/* Module 1: Prakriti assessment status */}
        <div className="card patient-module-card">
          <div>
            <div className="patient-module-header">
              <Sparkles size={22} />
              <h3>Prakriti Constitution</h3>
            </div>
            <div className="patient-module-body">
              {currentPatient.prakriti ? (
                <>
                  <p>The patient has been assessed. Dominant constitution profile:</p>
                  <div className="patient-module-status patient-module-status--completed">
                    Dominant: {currentPatient.prakriti}
                  </div>
                </>
              ) : (
                <>
                  <p>The patient's elemental constitution has not been assessed yet. Complete the questionnaire to unlock personalized treatments.</p>
                  <div className="patient-module-status patient-module-status--pending">
                    Not Assessed
                  </div>
                </>
              )}
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/treatment/assess')}
            style={{ width: '100%' }}
          >
            {currentPatient.prakriti ? 'Re-assess Prakriti' : 'Start Assessment'} <ArrowRight size={16} />
          </button>
        </div>

        {/* Module 2: AI Treatment Recommender */}
        <div className="card patient-module-card">
          <div>
            <div className="patient-module-header">
              <Briefcase size={22} />
              <h3>AYUSH Recommender</h3>
            </div>
            <div className="patient-module-body">
              <p>
                Generate evidence-based clinical recommendations (herbs, diet, yoga) tailored to the patient's constitution and clinical conditions.
              </p>
              {!currentPatient.prakriti && (
                <div style={{ display: 'flex', gap: '0.4rem', color: 'var(--accent-orange)', fontSize: '0.85rem', marginTop: '0.5rem', alignItems: 'center' }}>
                  <AlertCircle size={16} /> Prakriti assessment recommended first
                </div>
              )}
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/treatment/recommendations')}
            style={{ width: '100%' }}
          >
            Get Recommendations <ArrowRight size={16} />
          </button>
        </div>

        {/* Module 3: Active Treatment Plans */}
        <div className="card patient-module-card" style={{ gridColumn: 'span 1' }}>
          <div>
            <div className="patient-module-header">
              <ClipboardList size={22} />
              <h3>Treatment Plans</h3>
            </div>
            <div className="patient-module-body" style={{ maxHeight: '110px', overflowY: 'auto' }}>
              {loadingPlans ? (
                <p>Loading plans...</p>
              ) : patientPlans.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {patientPlans.slice(0, 2).map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => navigate(`/treatment/plans/${plan.id}`)}
                      style={{
                        padding: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {plan.conditionName}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {new Date(plan.createdDate).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                  {patientPlans.length > 2 && (
                    <span style={{ fontSize: '0.85rem', color: 'var(--primary-color)' }}>
                      +{patientPlans.length - 2} more plan(s)
                    </span>
                  )}
                </div>
              ) : (
                <p>No treatment plans saved for this patient yet.</p>
              )}
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/treatment/plans')}
            style={{ width: '100%' }}
          >
            Manage Treatment Plans <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="treatment-page">
      {currentPatient ? renderDashboardInner() : renderDashboardOuter()}
    </div>
  );
};

export default TreatmentDashboard;
